<script setup>
import {
  computed,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import DataImportsAPI from 'dashboard/api/dataImports';
import DatabaseExportAPI from 'dashboard/api/databaseExport';
import DatabaseImportAPI from 'dashboard/api/databaseImport';
import NewImportDialog from './NewImportDialog.vue';
import { importSourceFor } from './importSources';
import {
  POLL_INTERVAL_MS,
  formatDate,
  formatStatus,
  importedCount,
  isActiveImport,
  isActiveIntegrationImport,
  statusDotClass,
} from './importStatus';

const { t } = useI18n();
const getters = useStoreGetters();
const router = useRouter();

const dataImports = ref([]);
const isLoading = ref(true);
const isRefreshing = ref(false);
const isPolling = ref(false);
const showImportDrawer = ref(false);
const activeTab = ref('import');
const isExporting = ref(false);
const isImportingDb = ref(false);
const showImportDbConfirm = ref(false);
const selectedDbFile = ref(null);
const fileInputRef = ref(null);
const importDbDialogRef = ref(null);
const importProgress = ref(null);
const isResetting = ref(false);
const showResetConfirm = ref(false);
const resetDialogRef = ref(null);
let importPollTimer = null;
let pollTimer;
let isPageActive = false;

const accountId = getters.getCurrentAccountId;
const currentUser = getters.getCurrentUser;
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

const tabs = computed(() => [
  { key: 'import', label: t('DATA_IMPORTS.TABS.IMPORT') },
  ...(isSuperAdmin.value
    ? [{ key: 'export', label: t('DATA_IMPORTS.TABS.EXPORT') }]
    : []),
]);

const activeTabIndex = computed(() =>
  tabs.value.findIndex(tab => tab.key === activeTab.value)
);

const hasActiveImport = computed(() => dataImports.value.some(isActiveImport));
const hasActiveIntegrationImport = computed(() =>
  dataImports.value.some(isActiveIntegrationImport)
);

const dataImportRoute = dataImport => ({
  name: 'settings_data_import_show',
  params: { accountId: accountId.value, dataImportId: dataImport.id },
});

const importTypesFor = dataImport =>
  dataImport.import_types?.length
    ? dataImport.import_types
    : [dataImport.data_type];

const importTypeLabel = dataImport =>
  importTypesFor(dataImport)
    .map(type => {
      if (type === 'contacts') return t('DATA_IMPORTS.TYPES.CONTACTS');
      if (type === 'conversations') {
        return t('DATA_IMPORTS.TYPES.CONVERSATIONS');
      }
      return type;
    })
    .join(', ');

const fetchImports = async () => {
  const response = await DataImportsAPI.get();
  dataImports.value = response.data.payload || [];
};

const stopPolling = () => {
  if (!pollTimer) return;

  window.clearInterval(pollTimer);
  pollTimer = null;
};

const refreshImportsInBackground = async () => {
  if (
    !isPageActive ||
    isPolling.value ||
    !hasActiveImport.value ||
    document.hidden
  ) {
    return;
  }

  isPolling.value = true;
  try {
    await fetchImports();
  } finally {
    isPolling.value = false;
    if (!hasActiveImport.value) stopPolling();
  }
};

const startPolling = () => {
  stopPolling();
  if (!isPageActive || !hasActiveImport.value) return;

  pollTimer = window.setInterval(refreshImportsInBackground, POLL_INTERVAL_MS);
};

const refresh = async ({ showLoader = true } = {}) => {
  if (showLoader) isLoading.value = true;
  else isRefreshing.value = true;

  try {
    await fetchImports();
  } finally {
    isLoading.value = false;
    isRefreshing.value = false;
    if (isPageActive) {
      if (hasActiveImport.value && !pollTimer) startPolling();
      if (!hasActiveImport.value) stopPolling();
    }
  }
};

const openImport = dataImport => {
  router.push(dataImportRoute(dataImport));
};

const openImportDrawer = () => {
  if (!hasActiveIntegrationImport.value) showImportDrawer.value = true;
};

const onImportCreated = dataImportId => {
  showImportDrawer.value = false;
  router.push({
    name: 'settings_data_import_show',
    params: { accountId: accountId.value, dataImportId },
  });
};

const onTabChanged = tab => {
  activeTab.value = tab.key;
};

const extractFilename = response => {
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  return match ? match[1] : `chatwoot_db_${Date.now()}.dump`;
};

const exportDatabase = async () => {
  isExporting.value = true;
  try {
    const response = await DatabaseExportAPI.create();
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = extractFilename(response);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(t('DATA_IMPORTS.EXPORT.ERROR'));
  } finally {
    isExporting.value = false;
  }
};

const triggerDbImport = () => {
  fileInputRef.value?.click();
};

const handleFileSelect = event => {
  const file = event.target.files?.[0];
  if (file) {
    selectedDbFile.value = file;
    showImportDbConfirm.value = true;
    importDbDialogRef.value?.open();
  }
  event.target.value = '';
};

const cancelDbImport = () => {
  showImportDbConfirm.value = false;
  selectedDbFile.value = null;
};

const stopImportPolling = () => {
  if (importPollTimer) {
    window.clearInterval(importPollTimer);
    importPollTimer = null;
  }
};

const pollImportProgress = progressKey => {
  stopImportPolling();
  importPollTimer = window.setInterval(async () => {
    try {
      const { data } = await DatabaseImportAPI.getProgress(progressKey);
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      importProgress.value = parsed;
      if (parsed.status === 'completed') {
        stopImportPolling();
        isImportingDb.value = false;
        useAlert(t('DATA_IMPORTS.IMPORT_DB.SUCCESS'));
      } else if (parsed.status === 'failed') {
        stopImportPolling();
        isImportingDb.value = false;
        useAlert(t('DATA_IMPORTS.IMPORT_DB.ERROR'));
      }
    } catch (error) {
      const status = error?.response?.status;
      if ([401, 403, 404].includes(status)) {
        stopImportPolling();
        isImportingDb.value = false;
        useAlert(t('DATA_IMPORTS.IMPORT_DB.POLLING_STOPPED'));
      }
    }
  }, 3000);
};

const confirmDbImport = async () => {
  const file = selectedDbFile.value;
  importDbDialogRef.value?.close();
  isImportingDb.value = true;
  importProgress.value = { current: 0, total: 0, status: 'processing' };
  try {
    const { data } = await DatabaseImportAPI.create(file);
    pollImportProgress(data.progress_key);
  } catch (error) {
    useAlert(error?.response?.data?.error || t('DATA_IMPORTS.IMPORT_DB.ERROR'));
    isImportingDb.value = false;
  } finally {
    selectedDbFile.value = null;
  }
};

const triggerResetData = () => {
  showResetConfirm.value = true;
  resetDialogRef.value?.open();
};

const cancelResetData = () => {
  showResetConfirm.value = false;
};

const confirmResetData = async () => {
  resetDialogRef.value?.close();
  isResetting.value = true;
  try {
    await DatabaseImportAPI.resetData();
    useAlert(t('DATA_IMPORTS.RESET_DATA.SUCCESS'));
    window.location.reload();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('DATA_IMPORTS.RESET_DATA.ERROR')
    );
  } finally {
    isResetting.value = false;
  }
};

const handleVisibilityChange = () => {
  if (isPageActive && !document.hidden && hasActiveImport.value) {
    refreshImportsInBackground();
  }
};

onActivated(async () => {
  isPageActive = true;
  await refresh();
  if (!isPageActive) return;

  startPolling();
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onDeactivated(() => {
  isPageActive = false;
  stopPolling();
  stopImportPolling();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

onBeforeUnmount(() => {
  isPageActive = false;
  stopPolling();
  stopImportPolling();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('DATA_IMPORTS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('DATA_IMPORTS.HEADER')"
        :description="$t('DATA_IMPORTS.DESCRIPTION')"
      >
        <template #tabs>
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            @tab-changed="onTabChanged"
          />
        </template>
        <template v-if="activeTab === 'import' && dataImports.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('DATA_IMPORTS.TABLE.COUNT', { count: dataImports.length }) }}
          </span>
        </template>
        <template v-if="activeTab === 'import'" #actions>
          <span
            v-if="hasActiveImport"
            class="hidden items-center gap-1.5 text-body-main text-n-slate-11 sm:inline-flex"
          >
            <span class="size-2 rounded-full bg-n-teal-9 animate-pulse" />
            {{
              $t('DATA_IMPORTS.MONITOR.LIVE', {
                seconds: POLL_INTERVAL_MS / 1000,
              })
            }}
          </span>
          <Button
            ghost
            slate
            size="sm"
            icon="i-lucide-refresh-cw"
            :is-loading="isRefreshing"
            :aria-label="$t('DATA_IMPORTS.MONITOR.REFRESH')"
            :title="$t('DATA_IMPORTS.MONITOR.REFRESH')"
            @click="refresh({ showLoader: false })"
          />
          <Button
            size="sm"
            :label="$t('DATA_IMPORTS.TABLE.NEW_IMPORT')"
            :disabled="hasActiveIntegrationImport"
            :title="
              hasActiveIntegrationImport
                ? $t('DATA_IMPORTS.DRAWER.ACTIVE_IMPORT')
                : undefined
            "
            @click="openImportDrawer"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        v-if="activeTab === 'export'"
        class="flex min-h-80 flex-col items-center justify-center gap-4 rounded-xl border border-n-weak bg-n-solid-1 px-6 py-16 text-center"
      >
        <span
          class="flex size-12 items-center justify-center rounded-full bg-n-alpha-2"
        >
          <Icon icon="i-lucide-database" class="size-5 text-n-slate-11" />
        </span>
        <div class="flex flex-col gap-1">
          <h3 class="text-heading-2 text-n-slate-12">
            {{ $t('DATA_IMPORTS.EXPORT.TITLE') }}
          </h3>
          <p class="max-w-sm text-body-main text-n-slate-11">
            {{ $t('DATA_IMPORTS.EXPORT.DB_DESCRIPTION') }}
          </p>
        </div>
        <Button
          size="sm"
          icon="i-lucide-download"
          :label="$t('DATA_IMPORTS.EXPORT.DOWNLOAD_DB')"
          :is-loading="isExporting"
          @click="exportDatabase"
        />
      </div>

      <div
        v-else-if="!dataImports.length"
        class="flex min-h-80 flex-col items-center justify-center gap-4 rounded-xl border border-n-weak bg-n-solid-1 px-6 py-16 text-center"
      >
        <span
          class="flex size-12 items-center justify-center rounded-full bg-n-alpha-2"
        >
          <Icon icon="i-lucide-database" class="size-5 text-n-slate-11" />
        </span>
        <div class="flex flex-col gap-1">
          <h3 class="text-heading-2 text-n-slate-12">
            {{ $t('DATA_IMPORTS.TABLE.EMPTY') }}
          </h3>
          <p class="max-w-sm text-body-main text-n-slate-11">
            {{ $t('DATA_IMPORTS.TABLE.EMPTY_DESCRIPTION') }}
          </p>
        </div>
        <Button
          size="sm"
          icon="i-lucide-download"
          :label="$t('DATA_IMPORTS.TABLE.NEW_IMPORT')"
          @click="openImportDrawer"
        />
      </div>

      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="dataImport in dataImports"
          :key="dataImport.id"
          class="group flex cursor-pointer items-center justify-between gap-4 py-4"
          role="button"
          tabindex="0"
          @click="openImport(dataImport)"
          @keydown.enter="openImport(dataImport)"
          @keydown.space.prevent="openImport(dataImport)"
        >
          <div class="flex min-w-0 items-center gap-3">
            <img
              v-if="importSourceFor(dataImport).icon"
              v-tooltip.top="importSourceFor(dataImport).label"
              :src="importSourceFor(dataImport).icon"
              alt=""
              class="size-10 justify-center bg-n-alpha-3 rounded-xl shrink-0 object-contain border border-n-strong"
            />
            <span
              v-else
              v-tooltip.top="importSourceFor(dataImport).label"
              class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <Icon
                :icon="importSourceFor(dataImport).iconClass"
                class="size-4"
              />
            </span>
            <div class="flex min-w-0 flex-col gap-1">
              <div class="flex items-center gap-2">
                <span class="truncate text-heading-3 text-n-slate-12">
                  {{ dataImport.name || $t('DATA_IMPORTS.TABLE.UNNAMED') }}
                </span>
                <span class="flex shrink-0 items-center gap-1.5">
                  <span
                    class="size-2 rounded-full"
                    :class="[
                      statusDotClass(dataImport.status),
                      { 'animate-pulse': isActiveImport(dataImport) },
                    ]"
                  />
                  <span
                    class="whitespace-nowrap capitalize text-body-main text-n-slate-11"
                  >
                    {{ formatStatus(dataImport.status) }}
                  </span>
                </span>
              </div>
              <div
                class="flex flex-wrap items-center gap-2 text-body-main text-n-slate-11"
              >
                <span>{{ importTypeLabel(dataImport) }}</span>
                <div class="h-3 w-px rounded-lg bg-n-strong" />
                <span class="tabular-nums">
                  {{
                    $t('DATA_IMPORTS.TABLE.IMPORTED_COUNT', {
                      count: importedCount(dataImport),
                    })
                  }}
                </span>
                <div class="h-3 w-px rounded-lg bg-n-strong" />
                <span>{{ formatDate(dataImport.created_at) }}</span>
              </div>
            </div>
          </div>
          <Button
            v-tooltip.top="$t('DATA_IMPORTS.TABLE.VIEW')"
            icon="i-lucide-eye"
            slate
            sm
            class="shrink-0"
            @click.stop="openImport(dataImport)"
          />
        </div>
      </div>

      <div
        v-if="activeTab === 'import' && isSuperAdmin"
        class="mt-4 flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 px-6 py-8"
      >
        <div class="flex items-center gap-3">
          <span
            class="flex size-10 items-center justify-center rounded-full bg-n-alpha-2"
          >
            <Icon icon="i-lucide-database-zap" class="size-5 text-n-slate-11" />
          </span>
          <div class="flex flex-col">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ $t('DATA_IMPORTS.IMPORT_DB.TITLE') }}
            </h3>
            <p class="text-body-main text-n-slate-11">
              {{ $t('DATA_IMPORTS.IMPORT_DB.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div class="flex flex-col items-start gap-3">
          <Button
            ghost
            size="sm"
            icon="i-lucide-upload"
            :label="$t('DATA_IMPORTS.IMPORT_DB.BUTTON')"
            :is-loading="isImportingDb"
            :disabled="isImportingDb"
            @click="triggerDbImport"
          />
          <input
            ref="fileInputRef"
            type="file"
            accept=".dump"
            class="hidden"
            @change="handleFileSelect"
          />
          <div
            v-if="isImportingDb && importProgress"
            class="flex w-full max-w-md flex-col gap-2"
          >
            <div
              class="flex items-center justify-between text-label-small text-n-slate-11"
            >
              <span>{{ $t('DATA_IMPORTS.IMPORT_DB.PROGRESS') }}</span>
              <span v-if="importProgress.total > 0">
                {{ importProgress.current }} / {{ importProgress.total }}
              </span>
            </div>
            <div class="h-2 w-full overflow-hidden rounded-full bg-n-alpha-2">
              <div
                class="h-full rounded-full bg-n-brand transition-all duration-300"
                :style="{
                  width:
                    importProgress.total > 0
                      ? `${(importProgress.current / importProgress.total) * 100}%`
                      : '0%',
                }"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="activeTab === 'import'"
        class="mt-4 flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 px-6 py-8"
      >
        <div class="flex items-center gap-3">
          <span
            class="flex size-10 items-center justify-center rounded-full bg-n-ruby-9/10"
          >
            <Icon icon="i-lucide-trash-2" class="size-5 text-n-ruby-11" />
          </span>
          <div class="flex flex-col">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ $t('DATA_IMPORTS.RESET_DATA.TITLE') }}
            </h3>
            <p class="text-body-main text-n-slate-11">
              {{ $t('DATA_IMPORTS.RESET_DATA.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <div class="flex items-start gap-3">
          <Button
            ghost
            size="sm"
            icon="i-lucide-trash-2"
            :label="$t('DATA_IMPORTS.RESET_DATA.BUTTON')"
            :is-loading="isResetting"
            :disabled="isResetting"
            @click="triggerResetData"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>

  <Dialog
    ref="importDbDialogRef"
    type="alert"
    :title="$t('DATA_IMPORTS.IMPORT_DB.CONFIRM_TITLE')"
    :description="$t('DATA_IMPORTS.IMPORT_DB.CONFIRM_MESSAGE')"
    :confirm-button-label="$t('DATA_IMPORTS.IMPORT_DB.CONFIRM_BUTTON')"
    :is-loading="isImportingDb"
    @confirm="confirmDbImport"
    @close="cancelDbImport"
  >
    <div v-if="selectedDbFile" class="rounded-lg bg-n-alpha-2 px-3 py-2">
      <p class="text-sm text-n-slate-11">
        {{ selectedDbFile.name }}
        ({{ (selectedDbFile.size / 1024 / 1024).toFixed(1) }}
        {{ $t('DATA_IMPORTS.IMPORT_DB.FILE_SIZE') }})
      </p>
    </div>
  </Dialog>

  <Dialog
    ref="resetDialogRef"
    type="alert"
    :title="$t('DATA_IMPORTS.RESET_DATA.CONFIRM_TITLE')"
    :description="$t('DATA_IMPORTS.RESET_DATA.CONFIRM_MESSAGE')"
    :confirm-button-label="$t('DATA_IMPORTS.RESET_DATA.CONFIRM_BUTTON')"
    :is-loading="isResetting"
    @confirm="confirmResetData"
    @close="cancelResetData"
  />

  <NewImportDialog
    :show="showImportDrawer"
    :has-active-import="hasActiveIntegrationImport"
    @close="showImportDrawer = false"
    @created="onImportCreated"
  />
</template>
